import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Lock, Eye, EyeOff, Play, Heart, Shield, AlertCircle, BookOpen, ExternalLink, DollarSign, Calendar, Users, Upload, Check, Send, MessageSquare, Phone, Mail, X } from 'lucide-react';
import { siteConfig } from '@/config/site';
import { useLanguage } from '@/context/LanguageContext';
import { api } from '@/services/api';
import AcknowledgmentModal from '@/components/AcknowledgmentModal';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import DateTimePickerModal from '@/components/DateTimePickerModal';
import AmbientParticles from '@/components/AmbientParticles';

export function PrivatePage() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  
  // Check if user already passed auth
  const hasAccess = sessionStorage.getItem('casa_private_access');
  const hasAcknowledged = localStorage.getItem('casa_private_acknowledgment');
  
  // Flow: animation -> acknowledgment -> password -> content
  const [stage, setStage] = useState(
    hasAccess ? 'content' : 
    hasAcknowledged ? 'password' : 
    'animation'
  );
  const [animationComplete, setAnimationComplete] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(false);
  
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);

  // Application form state
  const [formData, setFormData] = useState({
    stage_name: '',
    real_name: '',
    age_confirmed: false,
    contact_preference: 'text',
    contact_info: '',
    email: '',
    visit_type: '',
    visit_datetime: '',
    visit_formatted: '',
    availability_days: '',
    availability_times: '',
    experience: '',
    city_transport: '',
    referral_name: '',
    referral_contact: '',
    profile_photo_url: '',
    photo_urls: [],
    nda_signed: false,
    terms_accepted: false,
  });
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  useEffect(() => {
    if (stage === 'content') {
      api.trackEvent('page_view', 'private', '/private');
    }
  }, [stage]);

  // Functions to handle animation completion
  const handleAnimationComplete = () => {
    setAnimationComplete(true);
    setTimeout(() => {
      setStage('acknowledgment');
      setAnimationComplete(false);
    }, 500);
  };

  const handlePostPasswordAnimationComplete = () => {
    setAnimationComplete(true);
    setTimeout(() => {
      sessionStorage.setItem('casa_private_access', 'true');
      setStage('content');
      setAnimationComplete(false);
    }, 500);
  };

  // Handle animation playback
  useEffect(() => {
    if ((stage === 'animation' || stage === 'post-password-animation') && videoRef.current) {
      const attemptPlay = async () => {
        try {
          videoRef.current.currentTime = 0;
          await videoRef.current.play();
        } catch (err) {
          console.log('Autoplay prevented');
          setShowPlayButton(true);
        }
      };
      
      if (videoRef.current.readyState >= 3) {
        attemptPlay();
      } else {
        videoRef.current.addEventListener('canplay', attemptPlay, { once: true });
      }
      
      // Timeout fallback
      const timeout = setTimeout(() => {
        if (stage === 'animation') {
          handleAnimationComplete();
        } else if (stage === 'post-password-animation') {
          handlePostPasswordAnimationComplete();
        }
      }, 8000);
      
      return () => clearTimeout(timeout);
    }
  }, [stage]);

  const handleManualPlay = async () => {
    setShowPlayButton(false);
    if (videoRef.current) {
      try {
        await videoRef.current.play();
      } catch (err) {
        if (stage === 'animation') {
          handleAnimationComplete();
        } else {
          handlePostPasswordAnimationComplete();
        }
      }
    }
  };

  const handleAcknowledgmentAccept = () => {
    setStage('password');
  };

  const handleAcknowledgmentDecline = () => {
    navigate('/');
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setError('');
    
    if (password.toLowerCase() === 'casa') {
      api.trackEvent('auth_success', 'private_password', '/private');
      setStage('post-password-animation');
      setShowPlayButton(false);
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      api.trackEvent('auth_failed', 'private_password', '/private');
      
      setError(language === 'en' 
        ? `Incorrect password. ${3 - newAttempts} attempts remaining.`
        : `Contraseña incorrecta. ${3 - newAttempts} intentos restantes.`
      );
      
      if (newAttempts >= 3) {
        setTimeout(() => navigate('/'), 2000);
      }
    }
  };

  // Form handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await api.uploadFile(file);
      setFormData((prev) => ({ ...prev, profile_photo_url: result.url }));
    } catch (error) {
      console.error('Profile photo upload error:', error);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      try {
        const result = await api.uploadFile(file);
        setUploadedPhotos(prev => [...prev, { url: result.url, filename: result.filename }]);
        setFormData(prev => ({
          ...prev,
          photo_urls: [...prev.photo_urls, result.url]
        }));
      } catch (error) {
        console.error('Upload error:', error);
      }
    }
  };

  const removePhoto = (index) => {
    setUploadedPhotos(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      photo_urls: prev.photo_urls.filter((_, i) => i !== index)
    }));
  };

  const handleDateTimeSelect = (selection) => {
    setFormData(prev => ({
      ...prev,
      visit_datetime: selection.isoDate,
      visit_formatted: selection.formatted,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Front-end gate to surface a clean message before round-trip
    if (!formData.age_confirmed) {
      alert(language === 'en' ? 'You must confirm you are 18 or older.' : 'Debes confirmar que tienes 18 años o más.');
      return;
    }
    if (!formData.terms_accepted) {
      alert(language === 'en' ? 'Please confirm you have read the Terms of Service.' : 'Confirma que has leído los Términos de Servicio.');
      return;
    }
    if (!formData.nda_signed) {
      alert(language === 'en' ? 'Please confirm you agree to the Non-Disclosure Agreement.' : 'Confirma que estás de acuerdo con el Acuerdo de Confidencialidad.');
      return;
    }
    if (!formData.real_name?.trim() || !formData.stage_name?.trim()) {
      alert(language === 'en' ? 'Real name and stage name are both required.' : 'El nombre real y el nombre artístico son obligatorios.');
      return;
    }
    if (!formData.profile_photo_url) {
      alert(language === 'en' ? 'A profile photo is required.' : 'Se requiere una foto de perfil.');
      return;
    }
    const additional = (formData.photo_urls || []).filter((u) => u);
    if (additional.length < 3) {
      alert(language === 'en' ? 'Please upload at least 3 additional photos beyond the profile photo.' : 'Sube al menos 3 fotos adicionales además de la foto de perfil.');
      return;
    }
    const isEmail = formData.contact_preference === 'email';
    if (!formData.contact_info?.trim()) {
      alert(language === 'en' ? 'Please provide your phone or email.' : 'Por favor proporciona tu teléfono o correo electrónico.');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.createApplication({
        real_name: formData.real_name.trim(),
        stage_name: formData.stage_name.trim(),
        age_confirmed: formData.age_confirmed,
        contact_method: formData.contact_preference || 'phone',
        phone: isEmail ? '' : formData.contact_info.trim(),
        email: isEmail ? formData.contact_info.trim() : (formData.email || ''),
        availability: formData.availability_days && formData.availability_times
          ? `${formData.availability_days} / ${formData.availability_times}`
          : 'Flexible',
        experience: formData.experience || '',
        city_transport: formData.city_transport || '',
        statement: formData.referral_name
          ? `Referral: ${formData.referral_name} - ${formData.referral_contact}. Visit: ${formData.visit_type || 'TBD'}`
          : `Visit preference: ${formData.visit_type || 'TBD'}`,
        profile_photo_url: formData.profile_photo_url,
        photo_urls: additional,
        house_visit_at: formData.visit_datetime || null,
        nda_signed: formData.nda_signed,
        acknowledgement_signed: formData.nda_signed,
        terms_accepted: formData.terms_accepted,
        privacy_accepted: formData.terms_accepted,
        signature_name: formData.real_name.trim(),
        signature_date: new Date().toISOString().split('T')[0],
        consent_version: 'v1.2026.02',
      });
      api.trackEvent('form_submit', 'application_submit', '/private');
      setIsSuccess(true);
    } catch (error) {
      console.error('Application error:', error);
      const detail = error?.response?.data?.detail || error?.data?.detail || error?.message;
      const friendly = Array.isArray(detail)
        ? detail.map((d) => d?.msg || JSON.stringify(d)).join(' · ')
        : (typeof detail === 'string' ? detail : null);
      alert(friendly || (language === 'en' ? 'There was an error submitting your application. Please try again.' : 'Hubo un error al enviar tu solicitud. Por favor intenta de nuevo.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const content = {
    en: {
      enterPassword: 'Enter Access Code',
      passwordPlaceholder: 'Enter code...',
      submit: 'Enter',
      back: 'Back to Home',
      subtitle: 'Exclusive access for referred members',
      header: 'Brand Orientation',
      pageSubtitle: 'Vixen — Join Casa de Candy',
      introTitle: 'Orientation · Read First',
      introText: 'This page is your official introduction to The House. If you\'re reading this, you\'ve been privately invited to preview Casa de Candy. This guide explains who we are, what we stand for, and how we expect our hosts to carry themselves.',
      introSubtext: 'Casa de Candy is built on a simple equation: beauty + discipline + safety + controlled environment. We handle the atmosphere, screening, and presentation. You bring presence, confidence, and professionalism.',
      wonderingTitle: "Earnings & Probation",
      wondering: [
        { icon: DollarSign, title: '50 / 50 — Experienced Vixens', text: 'Verified, experienced vixens take home 50% on every booking and 70% on tips. Your professionalism earns the higher split.' },
        { icon: DollarSign, title: '40 / 60 — Inexperienced Vixens', text: 'New vixens start at 40% to you, 60% to The House while we cover screening, marketing, security and onboarding. Tips are split 70 / 30 in your favour from day one.' },
        { icon: Calendar, title: '3-Month Probation', text: 'All new vixens — experienced or not — operate under a 3-month probationary period focused on conduct, attendance, presentation, and brand fit. Splits and assignments are reviewed at the end of the period.' },
        { icon: BookOpen, title: 'Employee Handbook · October 2025', text: 'The full Casa de Candy employee handbook (effective October 2025) is provided after orientation. Read it cover-to-cover before your first booking.' },
        { icon: Users, title: 'We Handle The Hard Stuff', text: 'Random calls, disorganized bookings, disrespectful guests, unclear expectations. Your role is to show up composed, attractive, direct, and respectful of yourself.' },
      ],
      supportTitle: 'Optional Support Resources',
      supportNote: '(Some may cost employee)',
      supportItems: ['Tax/financial guidance', 'Management coaching', 'Credit support guidance', 'Childcare help pathways'],
      employmentType: 'Employment type choice: Employee OR Contract Labor',
      brandOrientation: {
        title: 'What We Are',
        subtitle: 'Introduction to The House',
        description: 'The House is a structured, high-standards collective focused on brand, safety, consistency, and growth. We are not a free-for-all or a chaotic party. We are a carefully controlled environment where image, timing, and experience matter.',
        items: [
          { icon: Heart, title: 'What We Focus On', text: 'Paid time in a curated, neon-lit environment. Visual presence, conversation, and atmosphere. Non-explicit companionship and hosting.' },
          { icon: Shield, title: 'What We Don\'t Do', text: 'We do not arrange, promise, or encourage anything illegal. We do not sell or market explicit acts. Boundaries are set by you and enforced by The House.' },
          { icon: Eye, title: 'Our Promise', text: 'We don\'t ask you to change who you are. We amplify you with system, discipline, and discretion.' },
        ],
      },
      privacy: {
        title: 'Privacy & Identity Protection',
        items: [
          'Stage names are used instead of real names',
          'Profiles are built with controlled images and minimal personal details',
          'Guest communication runs through House channels — not your personal number',
          'Any additional content you choose to create is opt-in, not required',
        ],
      },
      safety: {
        title: 'Safety Protocols',
        subtitle: 'Your Safety Comes First',
        items: [
          'Hosts always have the right to end an interaction if they feel disrespected or unsafe',
          'Private and living areas stay separate from any guest-facing space',
          'We encourage check-in messages and clear arrival/exit timestamps',
          'If something doesn\'t feel right, we\'d rather you walk away than "push through it"',
        ],
        note: 'If a guest crosses a line, the interaction ends. The House steps in to handle logistics, communication, and any future decisions regarding that guest.',
      },
      rules: {
        title: 'Standards & Accountability',
        subtitle: 'The Core 10',
        items: [
          'Show up on time and ready — reliability is the foundation of everything',
          'Honor the schedule blocks you agree to — respect everyone\'s time',
          'Keep communication sharp, direct, and respectful',
          'No gossip, no screenshots, no drama loops',
          'Discretion is paramount — client data stays confidential',
          'Brand standards — presentation and conduct must reflect Casa de Candy values',
          'No competing interests — loyalty to the system, no client redirection',
          'Zero tolerance for dishonesty or boundary violations',
          'A pattern of dishonesty or brand damage ends the relationship',
          'Trust is our currency — we protect it fiercely',
        ],
      },
      trial: {
        title: 'Preview / Trial Run',
        description: 'You don\'t have to commit long-term to understand how Casa de Candy works. We allow a light "trial" entry so you can see the flow.',
        items: [
          'You provide three photos — face optional',
          'If your face appears, we can mask it without altering your overall look',
          'We assign a stage name and build a controlled profile',
          'When a request comes in, you receive advance notice to accept or decline',
        ],
      },
      application: {
        title: 'Start Your Journey',
        subtitle: 'Apply / Submit for Review',
        form: {
          photos: 'Upload Photos (Optional)',
          photosHint: 'Upload clear, recent photos. Multiple images accepted.',
          stageName: 'Stage Name',
          stageNameHint: 'Required',
          realName: 'Real Name',
          realNameHint: 'Optional - for internal records only',
          age: 'I confirm I am 18 years or older',
          contactPref: 'Preferred Contact Method',
          contactInfo: 'Contact Info',
          visitType: 'Schedule Your House Visit',
          visitCasa: 'Visit Casa',
          weVisit: 'We Visit You',
          selectDateTime: 'Select Date & Time',
          availabilityDays: 'Availability (Days)',
          availabilityTimes: 'Availability (Times)',
          experience: 'Experience Level',
          referral: 'Referred by a friend?',
          referralName: "Friend's Name",
          referralContact: "Friend's Contact",
          submit: 'Submit Application',
        },
        daysOptions: ['Weekdays', 'Weekends', 'Any Day'],
        timesOptions: ['Morning', 'Afternoon', 'Evening', 'Overnight', 'Any Time'],
        experienceOptions: ['None / New', 'Some Experience'],
      },
      handbook: {
        title: 'Employee Handbook',
        subtitle: 'Review our complete operational guidelines, standards, and expectations',
        cta: 'Open Employee Handbook',
        attribution: 'Instruction by Casa de Candy',
      },
      success: {
        title: 'Application Received!',
        message: 'Thank you for your interest in Casa de Candy. We will review your application and contact you within 24-48 hours.',
        nextSteps: 'Next Steps',
        steps: ['Wait for our review (24-48 hours)', 'Brief screening call', 'Trial session scheduling'],
      },
    },
    es: {
      enterPassword: 'Ingresa el Código de Acceso',
      passwordPlaceholder: 'Ingresa código...',
      submit: 'Entrar',
      back: 'Volver al Inicio',
      subtitle: 'Acceso exclusivo para miembros referidos',
      header: 'Orientación de Marca',
      pageSubtitle: 'Vixen — Únete a Casa de Candy',
      introTitle: 'Orientación · Lee Primero',
      introText: 'Esta página es tu introducción oficial a La Casa. Si estás leyendo esto, has sido invitada privadamente a conocer Casa de Candy. Esta guía explica quiénes somos, qué representamos y cómo esperamos que nuestras anfitrionas se conduzcan.',
      introSubtext: 'Casa de Candy se basa en una ecuación simple: belleza + disciplina + seguridad + ambiente controlado. Nosotros manejamos el ambiente, filtrado y presentación. Tú traes presencia, confianza y profesionalismo.',
      wonderingTitle: "Ganancias y Período de Prueba",
      wondering: [
        { icon: DollarSign, title: '50 / 50 — Vixens con Experiencia', text: 'Las vixens verificadas y con experiencia ganan el 50% en cada cita y el 70% en propinas. Tu profesionalismo gana la división más alta.' },
        { icon: DollarSign, title: '40 / 60 — Vixens Sin Experiencia', text: 'Las vixens nuevas comienzan con 40% para ti, 60% para La Casa mientras cubrimos filtrado, marketing, seguridad y orientación. Las propinas se dividen 70/30 a tu favor desde el primer día.' },
        { icon: Calendar, title: 'Prueba de 3 Meses', text: 'Todas las vixens nuevas, con o sin experiencia, operan bajo un período de prueba de 3 meses enfocado en conducta, asistencia, presentación y ajuste de marca. Las divisiones y asignaciones se revisan al final del período.' },
        { icon: BookOpen, title: 'Manual del Empleado · Octubre 2025', text: 'El manual completo del empleado de Casa de Candy (vigente desde octubre de 2025) se entrega después de la orientación.' },
        { icon: Users, title: 'Nosotros Manejamos Lo Difícil', text: 'Llamadas random, reservas desorganizadas, huéspedes irrespetuosos, expectativas confusas. Tu rol es presentarte compuesta, atractiva, directa y con respeto propio.' },
      ],
      supportTitle: 'Recursos de Apoyo Opcionales',
      supportNote: '(Algunos pueden tener costo)',
      supportItems: ['Orientación fiscal/financiera', 'Coaching de gestión', 'Orientación de crédito', 'Opciones de cuidado infantil'],
      employmentType: 'Tipo de empleo a elegir: Empleada O Trabajo por Contrato',
      brandOrientation: {
        title: 'Lo Que Somos',
        subtitle: 'Introducción a La Casa',
        description: 'La Casa es un colectivo estructurado de altos estándares enfocado en marca, seguridad, consistencia y crecimiento. No somos un desorden ni una fiesta caótica. Somos un ambiente cuidadosamente controlado donde la imagen, el tiempo y la experiencia importan.',
        items: [
          { icon: Heart, title: 'En Qué Nos Enfocamos', text: 'Tiempo pagado en un ambiente curado con neón. Presencia visual, conversación y ambiente. Compañía y hosting no explícito.' },
          { icon: Shield, title: 'Lo Que No Hacemos', text: 'No arreglamos, prometemos ni fomentamos nada ilegal. No vendemos ni comercializamos actos explícitos. Los límites los pones tú y los hace cumplir La Casa.' },
          { icon: Eye, title: 'Nuestra Promesa', text: 'No te pedimos que cambies quién eres. Te amplificamos con sistema, disciplina y discreción.' },
        ],
      },
      privacy: {
        title: 'Privacidad y Protección de Identidad',
        items: [
          'Se usan nombres artísticos en lugar de nombres reales',
          'Los perfiles se construyen con imágenes controladas y mínimos detalles personales',
          'La comunicación con huéspedes pasa por canales de La Casa — no tu número personal',
          'Cualquier contenido adicional que elijas crear es opcional, no requerido',
        ],
      },
      safety: {
        title: 'Protocolos de Seguridad',
        subtitle: 'Tu Seguridad es Primero',
        items: [
          'Las anfitrionas siempre tienen derecho a terminar una interacción si se sienten irrespetadas o inseguras',
          'Las áreas privadas y de vivienda se mantienen separadas de cualquier espacio para huéspedes',
          'Fomentamos mensajes de check-in y marcas claras de entrada/salida',
          'Si algo no se siente bien, preferimos que te retires a que "aguantes"',
        ],
        note: 'Si un huésped cruza una línea, la interacción termina. La Casa se encarga de la logística, comunicación y cualquier decisión futura respecto a ese huésped.',
      },
      rules: {
        title: 'Estándares y Responsabilidad',
        subtitle: 'Los 10 Fundamentales',
        items: [
          'Llega a tiempo y lista — la confiabilidad es la base de todo',
          'Honra los bloques de horario que aceptas — respeta el tiempo de todos',
          'Mantén la comunicación directa, clara y respetuosa',
          'Sin chismes, sin capturas de pantalla, sin dramas',
          'La discreción es primordial — los datos de clientes son confidenciales',
          'Estándares de marca — presentación y conducta deben reflejar valores de Casa de Candy',
          'Sin intereses en competencia — lealtad al sistema, sin redirección de clientes',
          'Tolerancia cero para deshonestidad o violaciones de límites',
          'Un patrón de deshonestidad o daño a la marca termina la relación',
          'La confianza es nuestra moneda — la protegemos ferozmente',
        ],
      },
      trial: {
        title: 'Vista Previa / Prueba',
        description: 'No tienes que comprometerte a largo plazo para entender cómo funciona Casa de Candy. Permitimos una entrada de "prueba" ligera para que veas el flujo.',
        items: [
          'Provees tres fotos — cara opcional',
          'Si tu cara aparece, podemos enmascararla sin alterar tu look general',
          'Asignamos un nombre artístico y construimos un perfil controlado',
          'Cuando llega una solicitud, recibes aviso previo para aceptar o rechazar',
        ],
      },
      application: {
        title: 'Comienza Tu Camino',
        subtitle: 'Aplicar / Enviar para Revisión',
        form: {
          photos: 'Subir Fotos (Opcional)',
          photosHint: 'Sube fotos claras y recientes. Se aceptan múltiples imágenes.',
          stageName: 'Nombre Artístico',
          stageNameHint: 'Requerido',
          realName: 'Nombre Real',
          realNameHint: 'Opcional - solo para registros internos',
          age: 'Confirmo que tengo 18 años o más',
          contactPref: 'Método de Contacto Preferido',
          contactInfo: 'Información de Contacto',
          visitType: 'Programa Tu Visita',
          visitCasa: 'Visitar Casa',
          weVisit: 'Te Visitamos',
          selectDateTime: 'Seleccionar Fecha y Hora',
          availabilityDays: 'Disponibilidad (Días)',
          availabilityTimes: 'Disponibilidad (Horarios)',
          experience: 'Nivel de Experiencia',
          referral: '¿Referida por una amiga?',
          referralName: 'Nombre de Amiga',
          referralContact: 'Contacto de Amiga',
          submit: 'Enviar Solicitud',
        },
        daysOptions: ['Entre Semana', 'Fines de Semana', 'Cualquier Día'],
        timesOptions: ['Mañana', 'Tarde', 'Noche', 'Madrugada', 'Cualquier Hora'],
        experienceOptions: ['Ninguna / Nueva', 'Algo de Experiencia'],
      },
      handbook: {
        title: 'Manual del Empleado',
        subtitle: 'Revisa nuestras guías operativas completas, estándares y expectativas',
        cta: 'Abrir Manual del Empleado',
        attribution: 'Instrucción por Casa de Candy',
      },
      success: {
        title: '¡Solicitud Recibida!',
        message: 'Gracias por tu interés en Casa de Candy. Revisaremos tu solicitud y te contactaremos en 24-48 horas.',
        nextSteps: 'Siguientes Pasos',
        steps: ['Espera nuestra revisión (24-48 horas)', 'Breve llamada de evaluación', 'Programación de sesión de prueba'],
      },
    },
  };

  const t = content[language];

  // Animation phase
  if (stage === 'animation' || stage === 'post-password-animation') {
    return (
      <div 
        className={`fixed inset-0 z-50 bg-[#07060B] flex items-center justify-center overflow-hidden transition-opacity duration-500 ${
          animationComplete ? 'opacity-0' : 'opacity-100'
        }`}
        data-testid="private-animation"
      >
        <video
          ref={videoRef}
          src={siteConfig.animations.page}
          className="w-full h-full object-contain max-w-5xl"
          playsInline
          muted
          preload="auto"
          onEnded={stage === 'animation' ? handleAnimationComplete : handlePostPasswordAnimationComplete}
          onError={stage === 'animation' ? handleAnimationComplete : handlePostPasswordAnimationComplete}
        />
        
        {showPlayButton && (
          <button
            onClick={handleManualPlay}
            className="absolute inset-0 flex flex-col items-center justify-center bg-[#07060B]/90 cursor-pointer group"
            data-testid="play-animation-btn"
          >
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#FF4DA6] to-[#8B5CFF] flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-[#FF4DA6]/30">
              <Play className="w-10 h-10 text-white ml-1" />
            </div>
            <p className="mt-4 text-[#A7A3BA] text-sm">Tap to play</p>
          </button>
        )}
        
        {!showPlayButton && (
          <button
            onClick={stage === 'animation' ? handleAnimationComplete : handlePostPasswordAnimationComplete}
            className="absolute bottom-8 right-8 px-4 py-2 text-sm text-[#A7A3BA] hover:text-[#F4F2FF] transition-colors opacity-0 animate-[fadeIn_0.5s_ease-out_2s_forwards]"
          >
            Skip →
          </button>
        )}
      </div>
    );
  }

  // Acknowledgment phase
  if (stage === 'acknowledgment') {
    return (
      <>
        <Helmet>
          <title>Vixen | Casa de Candy</title>
        </Helmet>
        <div className="min-h-screen bg-[#07060B]">
          <AcknowledgmentModal
            type="private"
            storageKey="casa_private_acknowledgment"
            onAccept={handleAcknowledgmentAccept}
            onDecline={handleAcknowledgmentDecline}
          />
        </div>
      </>
    );
  }

  // Password phase
  if (stage === 'password') {
    return (
      <>
        <Helmet>
          <title>Vixen Access | Casa de Candy</title>
        </Helmet>
        <div className="min-h-screen bg-[#07060B] flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="flex justify-center mb-8">
              <div className="relative animate-breathe">
                <div className="absolute inset-0 -m-8 bg-gradient-radial from-[#8B5CFF]/30 via-transparent to-transparent blur-xl" />
                <img 
                  src={siteConfig.brand.logoUrl}
                  alt="Casa de Candy"
                  className="w-40 h-auto object-contain relative z-10"
                  style={{ filter: 'drop-shadow(0 0 15px rgba(139, 92, 255, 0.8)) drop-shadow(0 0 30px rgba(255, 77, 166, 0.5))' }}
                />
              </div>
            </div>

            <div className="neon-card text-center" data-testid="password-gate">
              <div className="w-16 h-16 rounded-2xl bg-[#8B5CFF]/20 flex items-center justify-center mx-auto mb-6">
                <Lock className="w-8 h-8 text-[#8B5CFF]" />
              </div>
              
              <h2 className="font-heading text-2xl font-bold text-[#F4F2FF] mb-2">{t.enterPassword}</h2>
              <p className="text-[#A7A3BA] text-sm mb-6">{t.subtitle}</p>

              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t.passwordPlaceholder}
                    className="input-neon pr-12 text-center uppercase tracking-widest"
                    autoFocus
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A7A3BA] hover:text-[#F4F2FF]"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                {error && <p className="text-red-400 text-sm" data-testid="password-error">{error}</p>}

                <button type="submit" className="btn-neon btn-neon-pink w-full" disabled={!password} data-testid="password-submit">
                  {t.submit}
                </button>
              </form>

              <button onClick={() => navigate('/')} className="mt-6 text-[#A7A3BA] text-sm hover:text-[#F4F2FF] transition-colors">
                ← {t.back}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <>
        <Helmet><title>Application Received | Casa de Candy</title></Helmet>
        <div className="min-h-screen bg-[#07060B] flex flex-col animate-[fadeIn_0.8s_ease-out]">
          <Navbar />
          <main className="flex-1 flex items-center justify-center px-4 pt-24 pb-12">
            <div className="neon-card-glow text-center py-12 max-w-md w-full" data-testid="application-success">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#27D7FF] to-[#8B5CFF] flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-white" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-[#F4F2FF] mb-3">{t.success.title}</h3>
              <p className="text-[#A7A3BA] mb-8">{t.success.message}</p>
              <div className="neon-card bg-[#0B0A12] text-left">
                <h4 className="font-heading font-semibold text-[#F4F2FF] mb-3">{t.success.nextSteps}</h4>
                <ul className="space-y-2">
                  {t.success.steps.map((step, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-[#A7A3BA]">
                      <span className="text-[#FF4DA6]">{idx + 1}.</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Link to="/" className="btn-neon btn-neon-outline mt-6 inline-flex">
                {language === 'en' ? 'Back to Home' : 'Volver al Inicio'}
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  // Main content phase - Full orientation content
  return (
    <>
      <Helmet>
        <title>House Vixens | Casa de Candy</title>
        <meta name="description" content={siteConfig.seo.orientation.description} />
        <html lang={language} />
      </Helmet>

      <div className="min-h-screen bg-[#07060B] flex flex-col animate-[fadeIn_0.8s_ease-out]" data-testid="private-page">
        <Navbar />

        {/* Softly drifting ambient particles — subtle depth across the
            orientation experience without competing with content. */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
          <AmbientParticles count={28} testid="vixen-ambient-particles" />
        </div>
        
        <main className="flex-1 pt-24 relative z-10">
          {/* Hero Section */}
          <section id="about" className="py-12 px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-3xl mx-auto">
              <div className="flex justify-center mb-6">
                <div className="relative animate-breathe">
                  <div className="absolute inset-0 -m-8 bg-gradient-radial from-[#8B5CFF]/25 via-transparent to-transparent blur-xl" />
                  <img 
                    src={siteConfig.brand.logoUrl} 
                    alt="Casa de Candy"
                    className="w-48 sm:w-56 h-auto object-contain relative z-10"
                    style={{ filter: 'drop-shadow(0 0 15px rgba(139, 92, 255, 0.8)) drop-shadow(0 0 30px rgba(255, 77, 166, 0.5))' }}
                  />
                </div>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold text-gradient-pink mb-4" data-testid="private-hero-title">
                {t.header}
              </h1>
              <p className="text-[#A7A3BA] text-lg mb-8">{t.pageSubtitle}</p>
              
              {/* Intro Card */}
              <div className="neon-card-glow text-left max-w-2xl mx-auto">
                <h3 className="font-heading text-xl font-semibold text-[#FF4DA6] mb-3">{t.introTitle}</h3>
                <p className="text-[#F4F2FF] text-sm mb-4 leading-relaxed">{t.introText}</p>
                <p className="text-[#A7A3BA] text-sm leading-relaxed">{t.introSubtext}</p>
              </div>
            </div>
          </section>

          {/* Earnings & Split Section */}
          <section id="earnings" className="py-16 px-4 sm:px-6 lg:px-8" data-testid="earnings-section">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#F4F2FF] mb-4">{t.wonderingTitle}</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {t.wondering.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="neon-card flex gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#27D7FF]/20 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-[#27D7FF]" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-[#F4F2FF] mb-1">{item.title}</h3>
                        <p className="text-[#A7A3BA] text-sm">{item.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Support Resources */}
              <div className="neon-card">
                <h3 className="font-heading font-semibold text-[#F4F2FF] mb-1">{t.supportTitle}</h3>
                <p className="text-[#A7A3BA] text-xs mb-4">{t.supportNote}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {t.supportItems.map((item, idx) => (
                    <div key={idx} className="text-center p-3 rounded-xl bg-[#0B0A12] border border-white/5">
                      <span className="text-[#A7A3BA] text-xs">{item}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[#8B5CFF] text-sm mt-6 text-center font-medium">{t.employmentType}</p>
              </div>
            </div>
          </section>

          <div className="neon-divider" />

          {/* Brand Orientation Section */}
          <section id="orientation" className="py-16 px-4 sm:px-6 lg:px-8 bg-[#0B0A12]" data-testid="brand-orientation-section">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <p className="text-[#FF4DA6] text-sm font-semibold uppercase tracking-widest mb-2">{t.brandOrientation.subtitle}</p>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#F4F2FF] mb-4">{t.brandOrientation.title}</h2>
                <p className="text-[#A7A3BA] max-w-2xl mx-auto">{t.brandOrientation.description}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {t.brandOrientation.items.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="neon-card text-center" data-testid={`brand-item-${idx}`}>
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FF4DA6]/20 to-[#8B5CFF]/20 flex items-center justify-center mx-auto mb-4">
                        <Icon className="w-8 h-8 text-[#FF4DA6]" />
                      </div>
                      <h3 className="font-heading font-semibold text-[#F4F2FF] mb-2">{item.title}</h3>
                      <p className="text-[#A7A3BA] text-sm leading-relaxed">{item.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Privacy Section */}
          <section id="privacy" className="py-16 px-4 sm:px-6 lg:px-8" data-testid="privacy-section">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#F4F2FF]">{t.privacy.title}</h2>
              </div>
              <div className="neon-card border-[#8B5CFF]/20">
                <ul className="space-y-4">
                  {t.privacy.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#8B5CFF]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Eye className="w-3 h-3 text-[#8B5CFF]" />
                      </div>
                      <span className="text-[#F4F2FF] text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <div className="neon-divider" />

          {/* Safety Section */}
          <section id="safety" className="py-16 px-4 sm:px-6 lg:px-8 bg-[#0B0A12]" data-testid="safety-section">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <p className="text-[#FF4DA6] text-sm font-semibold uppercase tracking-widest mb-2">{t.safety.subtitle}</p>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#F4F2FF]">{t.safety.title}</h2>
              </div>
              <div className="neon-card border-[#FF4DA6]/20">
                <ul className="space-y-4 mb-6">
                  {t.safety.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#FF4DA6]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Shield className="w-3 h-3 text-[#FF4DA6]" />
                      </div>
                      <span className="text-[#F4F2FF] text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-4 border-t border-white/10">
                  <p className="text-[#A7A3BA] text-sm italic">{t.safety.note}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Trial Section */}
          <section id="trial" className="py-16 px-4 sm:px-6 lg:px-8" data-testid="trial-section">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#F4F2FF] mb-4">{t.trial.title}</h2>
                <p className="text-[#A7A3BA]">{t.trial.description}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {t.trial.items.map((item, idx) => (
                  <div key={idx} className="neon-card flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#27D7FF]/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-[#27D7FF]">{idx + 1}</span>
                    </div>
                    <span className="text-[#F4F2FF] text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <div className="neon-divider" />

          {/* Rules / Core 10 Section */}
          <section id="rules" className="py-16 px-4 sm:px-6 lg:px-8 bg-[#0B0A12]" data-testid="rules-section">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <p className="text-[#27D7FF] text-sm font-semibold uppercase tracking-widest mb-2">{t.rules.subtitle}</p>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#F4F2FF]">{t.rules.title}</h2>
              </div>
              <div className="neon-card border-[#27D7FF]/20">
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/10">
                  <div className="w-10 h-10 rounded-full bg-[#27D7FF]/20 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-[#27D7FF]" />
                  </div>
                  <span className="text-[#27D7FF] font-semibold uppercase tracking-wider text-sm">{t.rules.subtitle}</span>
                </div>
                <ul className="space-y-4">
                  {t.rules.items.map((rule, idx) => (
                    <li key={idx} className="flex items-start gap-3" data-testid={`rule-${idx}`}>
                      <span className="w-6 h-6 rounded-full bg-gradient-to-br from-[#27D7FF]/20 to-[#8B5CFF]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-[#27D7FF]">{idx + 1}</span>
                      </span>
                      <span className="text-[#F4F2FF] text-sm">{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Application Form Section */}
          <section id="apply" className="py-16 px-4 sm:px-6 lg:px-8" data-testid="application-section">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-12">
                <p className="text-[#8B5CFF] text-sm font-semibold uppercase tracking-widest mb-2">{t.application.subtitle}</p>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#F4F2FF]">{t.application.title}</h2>
              </div>

              <form onSubmit={handleSubmit} className="neon-card" data-testid="application-form">
                <div className="space-y-6">
                  {/* Stage Name */}
                  <div>
                    <label className="block text-sm font-medium text-[#A7A3BA] mb-2">
                      {t.application.form.stageName} <span className="text-[#FF4DA6]">*</span>
                    </label>
                    <input type="text" name="stage_name" value={formData.stage_name} onChange={handleChange} required className="input-neon" placeholder={t.application.form.stageNameHint} data-testid="app-stage-name-input" />
                  </div>

                  {/* Real Name (Optional) */}
                  <div>
                    <label className="block text-sm font-medium text-[#A7A3BA] mb-2">{t.application.form.realName}</label>
                    <input type="text" name="real_name" value={formData.real_name} onChange={handleChange} className="input-neon" placeholder={t.application.form.realNameHint} data-testid="app-real-name-input" />
                  </div>

                  {/* Age Confirmation */}
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-[#111022] border border-[#FF4DA6]/20">
                    <input type="checkbox" name="age_confirmed" checked={formData.age_confirmed} onChange={handleChange} required className="mt-1 w-5 h-5 rounded border-[#8B5CFF] bg-[#0B0A12] text-[#FF4DA6] focus:ring-[#FF4DA6]" data-testid="app-age-checkbox" />
                    <label className="text-[#F4F2FF] text-sm font-medium">
                      {t.application.form.age} <span className="text-[#FF4DA6]">*</span>
                    </label>
                  </div>

                  {/* Contact Preference */}
                  <div>
                    <label className="block text-sm font-medium text-[#A7A3BA] mb-3">
                      {t.application.form.contactPref} <span className="text-[#FF4DA6]">*</span>
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {['call', 'text', 'email', 'whatsapp'].map((pref) => (
                        <button
                          key={pref}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, contact_preference: pref }))}
                          className={`py-3 px-4 rounded-xl border text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                            formData.contact_preference === pref
                              ? 'bg-gradient-to-r from-[#FF4DA6]/20 to-[#8B5CFF]/20 border-[#FF4DA6] text-[#FF4DA6]'
                              : 'bg-[#0B0A12] border-white/10 text-[#A7A3BA] hover:border-[#FF4DA6]/50'
                          }`}
                          data-testid={`app-contact-${pref}`}
                        >
                          {pref === 'call' && <Phone className="w-4 h-4" />}
                          {pref === 'text' && <MessageSquare className="w-4 h-4" />}
                          {pref === 'email' && <Mail className="w-4 h-4" />}
                          {pref === 'whatsapp' && <MessageSquare className="w-4 h-4" />}
                          <span className="capitalize">{pref === 'whatsapp' ? 'WhatsApp' : pref}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div>
                    <label className="block text-sm font-medium text-[#A7A3BA] mb-2">
                      {t.application.form.contactInfo} <span className="text-[#FF4DA6]">*</span>
                    </label>
                    <input
                      type={formData.contact_preference === 'email' ? 'email' : 'tel'}
                      name="contact_info"
                      value={formData.contact_info}
                      onChange={handleChange}
                      required
                      className="input-neon"
                      placeholder={formData.contact_preference === 'email' ? 'your@email.com' : '(555) 555-5555'}
                      data-testid="app-contact-info-input"
                    />
                  </div>

                  {/* Visit Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-[#A7A3BA] mb-3">{t.application.form.visitType}</label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, visit_type: 'visit_casa' }))}
                        className={`py-4 px-4 rounded-xl border text-sm font-medium transition-all ${
                          formData.visit_type === 'visit_casa'
                            ? 'bg-gradient-to-r from-[#27D7FF]/20 to-[#8B5CFF]/20 border-[#27D7FF] text-[#27D7FF]'
                            : 'bg-[#0B0A12] border-white/10 text-[#A7A3BA] hover:border-[#27D7FF]/50'
                        }`}
                        data-testid="app-visit-casa"
                      >
                        {t.application.form.visitCasa}
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, visit_type: 'we_visit' }))}
                        className={`py-4 px-4 rounded-xl border text-sm font-medium transition-all ${
                          formData.visit_type === 'we_visit'
                            ? 'bg-gradient-to-r from-[#27D7FF]/20 to-[#8B5CFF]/20 border-[#27D7FF] text-[#27D7FF]'
                            : 'bg-[#0B0A12] border-white/10 text-[#A7A3BA] hover:border-[#27D7FF]/50'
                        }`}
                        data-testid="app-we-visit"
                      >
                        {t.application.form.weVisit}
                      </button>
                    </div>
                  </div>

                  {/* Date/Time Selection */}
                  {formData.visit_type && (
                    <div>
                      <label className="block text-sm font-medium text-[#A7A3BA] mb-2">{t.application.form.selectDateTime}</label>
                      <button
                        type="button"
                        onClick={() => setShowDatePicker(true)}
                        className="input-neon w-full text-left flex items-center justify-between cursor-pointer"
                        data-testid="app-datetime-btn"
                      >
                        <span className={formData.visit_formatted ? 'text-[#F4F2FF]' : 'text-[#A7A3BA]'}>
                          {formData.visit_formatted || t.application.form.selectDateTime}
                        </span>
                        <Calendar className="w-5 h-5 text-[#8B5CFF]" />
                      </button>
                    </div>
                  )}

                  {/* Availability */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#A7A3BA] mb-2">{t.application.form.availabilityDays}</label>
                      <select name="availability_days" value={formData.availability_days} onChange={handleChange} className="input-neon" data-testid="app-availability-days">
                        <option value="">{language === 'en' ? 'Select...' : 'Seleccionar...'}</option>
                        {t.application.daysOptions.map((opt, idx) => <option key={idx} value={opt}>{opt}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#A7A3BA] mb-2">{t.application.form.availabilityTimes}</label>
                      <select name="availability_times" value={formData.availability_times} onChange={handleChange} className="input-neon" data-testid="app-availability-times">
                        <option value="">{language === 'en' ? 'Select...' : 'Seleccionar...'}</option>
                        {t.application.timesOptions.map((opt, idx) => <option key={idx} value={opt}>{opt}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Experience */}
                  <div>
                    <label className="block text-sm font-medium text-[#A7A3BA] mb-2">{t.application.form.experience}</label>
                    <select name="experience" value={formData.experience} onChange={handleChange} className="input-neon" data-testid="app-experience-select">
                      <option value="">{language === 'en' ? 'Select...' : 'Seleccionar...'}</option>
                      {t.application.experienceOptions.map((opt, idx) => <option key={idx} value={opt}>{opt}</option>)}
                    </select>
                  </div>

                  {/* Referral */}
                  <div className="neon-card bg-[#0B0A12] border-[#8B5CFF]/20">
                    <p className="text-[#A7A3BA] text-sm mb-4">{t.application.form.referral}</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-[#A7A3BA] mb-1">{t.application.form.referralName}</label>
                        <input type="text" name="referral_name" value={formData.referral_name} onChange={handleChange} className="input-neon text-sm" data-testid="app-referral-name" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#A7A3BA] mb-1">{t.application.form.referralContact}</label>
                        <input type="tel" name="referral_contact" value={formData.referral_contact} onChange={handleChange} className="input-neon text-sm" data-testid="app-referral-contact" />
                      </div>
                    </div>
                  </div>

                  {/* Profile photo (required) */}
                  <div>
                    <label className="block text-sm font-medium text-[#A7A3BA] mb-2">
                      Profile Photo <span className="text-[#FF4DA6]">*</span>
                    </label>
                    {formData.profile_photo_url ? (
                      <div className="relative w-32 h-32 rounded-2xl overflow-hidden border border-[#FF4DA6]/40 bg-[#0B0A12]">
                        <img src={formData.profile_photo_url} alt="Profile" className="w-full h-full object-cover" data-testid="app-profile-photo-preview" />
                        <button
                          type="button"
                          onClick={() => setFormData((p) => ({ ...p, profile_photo_url: '' }))}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500/90 text-white flex items-center justify-center"
                          aria-label="Remove profile photo"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-white/20 rounded-xl p-6 text-center hover:border-[#FF4DA6]/50 transition-colors">
                        <input type="file" accept="image/*" onChange={handleProfilePhotoUpload} className="hidden" id="profile-photo-upload" data-testid="app-profile-photo-input" />
                        <label htmlFor="profile-photo-upload" className="cursor-pointer">
                          <Upload className="w-8 h-8 text-[#FF4DA6] mx-auto mb-2" />
                          <p className="text-[#A7A3BA] text-sm">Upload your primary profile photo</p>
                          <p className="text-[#A7A3BA]/70 text-xs mt-1">JPG or PNG · clear face shot</p>
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Additional photos (minimum 3) */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-[#A7A3BA]">
                        Additional Photos <span className="text-[#FF4DA6]">*</span>
                      </label>
                      <span className={`text-xs ${uploadedPhotos.length >= 3 ? 'text-green-400' : 'text-[#FFB6D8]'}`} data-testid="app-photo-count">
                        {uploadedPhotos.length} / 3 minimum
                      </span>
                    </div>
                    <div className="border-2 border-dashed border-white/20 rounded-xl p-6 text-center hover:border-[#FF4DA6]/50 transition-colors">
                      <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" id="photo-upload" data-testid="app-photo-input" />
                      <label htmlFor="photo-upload" className="cursor-pointer">
                        <Upload className="w-10 h-10 text-[#8B5CFF] mx-auto mb-3" />
                        <p className="text-[#A7A3BA] text-sm">Upload 3 to 4 additional photos. More are welcome.</p>
                      </label>
                    </div>
                    {uploadedPhotos.length > 0 && (
                      <div className="mt-4 grid grid-cols-3 gap-2">
                        {uploadedPhotos.map((photo, idx) => (
                          <div key={idx} className="relative aspect-square rounded-lg overflow-hidden bg-[#0B0A12] group">
                            <img src={photo.url} alt={`Upload ${idx + 1}`} className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => removePhoto(idx)}
                              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500/90 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Compliance acknowledgements (all required) */}
                  <div className="neon-card bg-[#0B0A12] border-[#FF4DA6]/20 space-y-3" data-testid="app-compliance-block">
                    <p className="text-xs uppercase tracking-wider text-[#FFB6D8]">Required acknowledgements</p>
                    <label className="flex items-start gap-3 cursor-pointer text-sm text-[#A7A3BA]">
                      <input
                        type="checkbox"
                        name="terms_accepted"
                        checked={formData.terms_accepted}
                        onChange={handleChange}
                        required
                        className="mt-0.5 w-4 h-4 accent-[#FF4DA6] flex-shrink-0"
                        data-testid="app-terms-checkbox"
                      />
                      <span>I have read and agree to the{' '}
                        <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#FFB6D8] underline underline-offset-2">Terms of Service</a> and{' '}
                        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#FFB6D8] underline underline-offset-2">Privacy Policy</a>.
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer text-sm text-[#A7A3BA]">
                      <input
                        type="checkbox"
                        name="nda_signed"
                        checked={formData.nda_signed}
                        onChange={handleChange}
                        required
                        className="mt-0.5 w-4 h-4 accent-[#8B5CFF] flex-shrink-0"
                        data-testid="app-nda-checkbox"
                      />
                      <span>I have read and agree to the{' '}
                        <a href="/nda" target="_blank" rel="noopener noreferrer" className="text-[#FFB6D8] underline underline-offset-2">Non-Disclosure Agreement</a>{' '}
                        and authorise its electronic signature using my legal name above.
                      </span>
                    </label>
                  </div>

                  {/* Submit */}
                  <button type="submit" disabled={isSubmitting} className="btn-neon btn-neon-pink w-full" data-testid="app-submit-btn">
                    {isSubmitting ? (
                      <span>{language === 'en' ? 'Submitting...' : 'Enviando...'}</span>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>{t.application.form.submit}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </section>

          {/* Employee Handbook Section */}
          <section id="handbook" className="py-16 px-4 sm:px-6 lg:px-8 bg-[#0B0A12]" data-testid="handbook-section">
            <div className="max-w-2xl mx-auto text-center">
              <div className="neon-card-glow">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#8B5CFF]/20 to-[#27D7FF]/20 flex items-center justify-center mx-auto mb-6">
                  <BookOpen className="w-10 h-10 text-[#8B5CFF]" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#F4F2FF] mb-3">{t.handbook.title}</h2>
                <p className="text-[#A7A3BA] mb-2 max-w-md mx-auto">{t.handbook.subtitle}</p>
                <p className="text-[#8B5CFF] text-xs mb-8">{t.handbook.attribution}</p>
                <a
                  href="https://customer-assets.emergentagent.com/job_97103200-75f7-4936-80be-6484367032ae/artifacts/vpamzufu_casa_de_candy_employee_handbook_v1.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-neon btn-neon-cyan inline-flex"
                  data-testid="open-handbook-btn"
                >
                  <BookOpen className="w-4 h-4" />
                  <span>{t.handbook.cta}</span>
                  <ExternalLink className="w-3 h-3 ml-1 opacity-70" />
                </a>
              </div>
            </div>
          </section>
        </main>

        <Footer />

        {/* Date/Time Picker Modal */}
        <DateTimePickerModal
          isOpen={showDatePicker}
          onClose={() => setShowDatePicker(false)}
          onSelect={handleDateTimeSelect}
          showServiceSelect={false}
        />
      </div>
    </>
  );
}

export default PrivatePage;
